import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AppendNoteDialogComponent } from '../../../append-note-dialog/append-note-dialog.component';
import { SlurryTestOutputSummaryComponent } from '../../popups/slurry-test-output-summary/slurry-test-output-summary.component';
import {
  CompStrRow,
  FoamStabilityRow,
  SlurryCapabilitiesDto,
  SlurryCapabilityItemDto
} from '../../../../services/slurry.service';
import { SlurryOutputActionContext, TestNoteRow } from '../../slurry-details.models';

interface FoamTestSharedSettings {
  bhst: number | null;
  baseDensity: number | null;
  foamDensity: number | null;
}

interface FoamTestAttachmentsRequest {
  testInputMasterId: number;
  title: string;
}

type FoamTestSectionKind = 'foamStability' | 'compressiveStrength';

@Component({
  selector: 'app-slurry-foam-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTooltipModule, SlurryTestOutputSummaryComponent],
  templateUrl: './slurry-foam-tab.component.html',
  styleUrls: ['./slurry-foam-tab.component.css']
})
export class SlurryFoamTabComponent implements OnChanges {
  @Input() testSettings: FoamTestSharedSettings = {
    bhst: null,
    baseDensity: null,
    foamDensity: null
  };
  @Input() foamCompStrTests: CompStrRow[] = [];
  @Input() foamStabilityTests: FoamStabilityRow[] = [];
  @Input() compressiveTypeOptions: ReadonlyArray<{ id: number; label: string; }> = [];
  @Input() crushTypeOptions: ReadonlyArray<{ id: number; label: string; }> = [];
  @Input() foamStabilityTypeOptions: ReadonlyArray<{ id: number; label: string; }> = [];
  @Input() capabilities: SlurryCapabilitiesDto | null = null;
  @Input() isParentProjectCompleted = false;
  @Input() statusId = 1;
  @Input() slurryId = 0;
  @Input() testsMessage = '';
  @Input() foamSettingsMessage = '';
  @Input() testsValidationError: string | null = null;
  @Input() showTestValidationErrors = false;
  @Input() testAttachmentCounts: Record<number, number> = {};

  @Output() updateBaseDensityRequested = new EventEmitter<void>();
  @Output() outputActionRequested = new EventEmitter<SlurryOutputActionContext>();
  @Output() singleTestRemoveRequested = new EventEmitter<number>();
  @Output() testAttachmentsRequested = new EventEmitter<FoamTestAttachmentsRequest>();

  blockedTestsActionMessage = '';
  blockedFoamSettingsActionMessage = '';

  private readonly compressiveStrengthDefaultRampTime = 4;
  private readonly compressiveStrengthDefaultLengthOfTest = 48;
  private previousFoamDensityDefault: number | null = null;
  private readonly sectionSelected: Record<FoamTestSectionKind, boolean> = {
    foamStability: false,
    compressiveStrength: false
  };

  constructor(private readonly dialog: MatDialog) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['testSettings']) {
      this.previousFoamDensityDefault = this.testSettings.foamDensity;
    }

    if (changes['foamStabilityTests'] || changes['foamCompStrTests']) {
      this.syncSectionSelectionFromRows();
    }
  }

  private getCapabilityAllowed(
    selector: (c: SlurryCapabilitiesDto) => SlurryCapabilityItemDto,
    fallback: boolean
  ): boolean {
    const item = this.capabilities ? selector(this.capabilities) : null;
    return item?.allowed ?? fallback;
  }

  private getCapabilityReason(
    selector: (c: SlurryCapabilitiesDto) => SlurryCapabilityItemDto
  ): string | null {
    const item = this.capabilities ? selector(this.capabilities) : null;
    if (!item || item.allowed || !item.reason) {
      return null;
    }

    return item.reason;
  }

  canSaveTests(): boolean {
    const fallback = !this.isParentProjectCompleted && (this.statusId === 1 || this.statusId === 6);
    return this.getCapabilityAllowed(c => c.saveTests, fallback);
  }

  canUpdateBaseDensity(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && (this.statusId === 2 || this.statusId === 3 || this.statusId === 7);
    return this.getCapabilityAllowed(c => c.updateBaseDensity, fallback);
  }

  canEditCrushLengthOfTest(): boolean {
    if (this.isTestsEditable) {
      return true;
    }

    return this.getCapabilityAllowed(c => c.editTestCrushLength, false);
  }

  canEditFoamBaseDensity(): boolean {
    return this.canSaveTests() || this.canUpdateBaseDensity();
  }

  get isTestsEditable(): boolean {
    return this.canSaveTests();
  }

  isFoamTestsActive(): boolean {
    return this.isSectionSelected('foamStability') || this.isSectionSelected('compressiveStrength');
  }

  isSectionVisible(kind: FoamTestSectionKind): boolean {
    return this.isTestsEditable || this.isSectionSelected(kind);
  }

  isSectionSelected(kind: FoamTestSectionKind): boolean {
    return this.sectionSelected[kind];
  }

  onSectionSelectionChanged(kind: FoamTestSectionKind, selected: boolean): void {
    this.clearBlockedActionMessages();
    this.sectionSelected[kind] = selected;
    if (!selected) {
      this.clearSectionRows(kind);
    }
  }

  canSubmitBaseDensityUpdate(): boolean {
    return this.canUpdateBaseDensity()
      && this.isFoamTestsActive()
      && this.testSettings.baseDensity != null
      && this.testsValidationError == null;
  }

  getSaveTestsBlockedReason(): string {
    if (this.canSaveTests()) {
      return '';
    }

    const metadataReason = this.getCapabilityReason(c => c.saveTests);
    if (metadataReason) {
      return metadataReason;
    }
    if (this.isParentProjectCompleted) {
      return 'Tests are read-only because the parent project is completed.';
    }

    return 'Tests can only be saved while the slurry is in Draft or Draft Again.';
  }

  private canAppendClearNotes(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && this.statusId === 3;
    return this.getCapabilityAllowed(c => c.appendClearNotes, fallback);
  }

  private getAppendClearNotesBlockedReason(): string {
    if (this.canAppendClearNotes()) {
      return '';
    }

    const metadataReason = this.getCapabilityReason(c => c.appendClearNotes);
    if (metadataReason) {
      return metadataReason;
    }
    if (this.isParentProjectCompleted) {
      return 'Append / Clear Note is blocked because the parent project is completed.';
    }
    if (this.slurryId <= 0) {
      return 'Save the slurry before appending or clearing notes.';
    }

    return 'Append / Clear Note is available only while the slurry is In Lab.';
  }

  getSubmitBaseDensityUpdateBlockedReason(): string {
    if (this.canSubmitBaseDensityUpdate()) {
      return '';
    }
    if (!this.isFoamTestsActive()) {
      return 'Select at least one foam test section first.';
    }
    if (!this.canUpdateBaseDensity()) {
      const metadataReason = this.getCapabilityReason(c => c.updateBaseDensity);
      if (metadataReason) {
        return metadataReason;
      }
      if (this.isParentProjectCompleted) {
        return 'Base density is read-only because the parent project is completed.';
      }
      if (this.slurryId <= 0) {
        return 'Save the slurry before updating base density.';
      }

      return 'Base density can only be updated while the slurry is Ready to Test, In Lab, or Sent to Lab Again.';
    }
    if (this.testSettings.baseDensity == null) {
      return 'Enter Base Density before updating it.';
    }

    return this.testsValidationError ?? '';
  }

  private isPersistedTestInputMaster(id: number | null | undefined): id is number {
    return typeof id === 'number' && id > 0;
  }

  canShowAppendClearNotes(testInputMasterId: number | null | undefined): boolean {
    return this.isPersistedTestInputMaster(testInputMasterId) && this.canAppendClearNotes();
  }

  canShowTransientTestRowRemove(_testInputMasterId: number | null | undefined): boolean {
    return this.isTestsEditable;
  }

  canShowRemoveSingleTest(testInputMasterId: number | null | undefined): boolean {
    if (!this.isPersistedTestInputMaster(testInputMasterId)) {
      return false;
    }

    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && this.statusId === 3;
    return this.getCapabilityAllowed(c => c.removeSingleTest, fallback);
  }

  getTestPrimaryOutput(outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): import('../../../../services/slurry.service').BatchTestBriefDto | null {
    if (!outputs?.length) {
      return null;
    }

    return outputs.reduce((latest, current) =>
      current.testOutputMasterId > latest.testOutputMasterId ? current : latest
    );
  }

  getTestOutputBlockedReason(outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): string {
    return this.getTestPrimaryOutput(outputs) ? '' : 'No batch output is recorded for this test yet.';
  }

  batchTestStatusLabel(statusNum: number): string {
    const map: Record<number, string> = {
      1: 'InProgress',
      2: 'Approved',
      3: 'Rejected',
      4: 'Repeat',
      5: 'Failed',
      6: 'Completed',
      7: 'Cancelled'
    };
    return map[statusNum] ?? String(statusNum);
  }

  isCompressiveStrengthUca(row: CompStrRow): boolean {
    return row.compressiveStrengthTypeId === 1;
  }

  isCompressiveStrengthCrush(row: CompStrRow): boolean {
    return row.compressiveStrengthTypeId === 2;
  }

  isRequiredFieldMissing(value: unknown): boolean {
    return this.showTestValidationErrors && (value == null || value === '');
  }

  isCompressiveStrengthFieldMissing(
    row: CompStrRow,
    field: 'type' | 'crushType' | 'rampTime' | 'pressure' | 'temperature' | 'lengthOfTest'
  ): boolean {
    if (!this.showTestValidationErrors) { return false; }
    switch (field) {
      case 'type':
        return !row.compressiveStrengthTypeId || !row.compressiveStrengthTestTypeId;
      case 'crushType':
        return this.isCompressiveStrengthCrush(row) && row.crushTestTypeId == null;
      case 'rampTime':
        return this.isCompressiveStrengthUca(row) && row.temperatureRampTime == null;
      case 'pressure':
        return row.pressure == null;
      case 'temperature':
        return row.temperature == null;
      case 'lengthOfTest':
        return this.isCompressiveStrengthCrush(row) && row.lengthOfTest == null;
    }
  }

  addFoamCompStr(): void {
    this.clearBlockedActionMessages();
    this.sectionSelected['compressiveStrength'] = true;
    this.foamCompStrTests.push(this.createDefaultCompStr(1, 2));
  }

  addFoamStab(): void {
    this.clearBlockedActionMessages();
    this.sectionSelected['foamStability'] = true;
    this.foamStabilityTests.push(this.createDefaultFoamStability());
  }

  removeFoamCompStr(index: number): void {
    this.clearBlockedActionMessages();
    this.foamCompStrTests.splice(index, 1);
  }

  removeFoamStab(index: number): void {
    this.clearBlockedActionMessages();
    this.foamStabilityTests.splice(index, 1);
  }

  onFoamDensityChanged(nextFoamDensity: number | null): void {
    const oldFoamDensity = this.previousFoamDensityDefault;
    this.testSettings.foamDensity = nextFoamDensity;

    if (nextFoamDensity === oldFoamDensity) {
      return;
    }

    for (const row of this.foamStabilityTests) {
      if (row.foamedDensity == null || (oldFoamDensity != null && row.foamedDensity === oldFoamDensity)) {
        row.foamedDensity = nextFoamDensity;
      }
    }

    this.previousFoamDensityDefault = nextFoamDensity;
  }

  onCompStrTypeChanged(row: CompStrRow): void {
    this.applyCompressiveStrengthTypeDefaults(row);
  }

  onBlockedActionClick(reason: string | null | undefined, section: 'tests' | 'foamSettings'): void {
    this.clearBlockedActionMessages();
    if (!reason) {
      return;
    }

    if (section === 'foamSettings') {
      this.blockedFoamSettingsActionMessage = reason;
      return;
    }

    this.blockedTestsActionMessage = reason;
  }

  getTestAttachmentCount(testInputMasterId: number | null | undefined): number {
    if (!this.isPersistedTestInputMaster(testInputMasterId)) {
      return 0;
    }

    return this.testAttachmentCounts[testInputMasterId] ?? 0;
  }

  getTestAttachmentsLabel(testInputMasterId: number | null | undefined): string {
    return `View Attachments (${this.getTestAttachmentCount(testInputMasterId)})`;
  }

  getTestAttachmentsBlockedReason(testInputMasterId: number | null | undefined): string {
    if (this.isPersistedTestInputMaster(testInputMasterId)) {
      return '';
    }

    return 'Save this test first to view its attachments.';
  }

  openTestNotesDialog(row: TestNoteRow): void {
    if (!this.isPersistedTestInputMaster(row.testInputMasterId)) {
      return;
    }

    const dialogRef = this.dialog.open(AppendNoteDialogComponent, {
      width: '960px',
      disableClose: true,
      data: {
        mode: 'test',
        slurryId: this.slurryId,
        testInputMasterId: row.testInputMasterId,
        originalNote: row.notes ?? ''
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result && typeof result.updatedNotes === 'string') {
        row.notes = result.updatedNotes;
      }
    });
  }

  reviewTestOutput(outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): void {
    this.emitOutputAction('review', outputs);
  }

  recordTestOutput(outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): void {
    this.emitOutputAction('record', outputs);
  }

  openTestBatch(outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): void {
    this.emitOutputAction('batch', outputs);
  }

  private emitOutputAction(action: SlurryOutputActionContext['action'], outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): void {
    if (outputs?.length) {
      this.outputActionRequested.emit({ action, outputs });
    }
  }

  removeSingleTest(testInputMasterId: number): void {
    this.singleTestRemoveRequested.emit(testInputMasterId);
  }

  openTestAttachments(testInputMasterId: number | null | undefined, title: string): void {
    if (!this.isPersistedTestInputMaster(testInputMasterId)) {
      return;
    }

    this.testAttachmentsRequested.emit({ testInputMasterId, title });
  }

  updateBaseDensity(): void {
    this.clearBlockedActionMessages();
    this.updateBaseDensityRequested.emit();
  }

  private createDefaultCompStr(compressiveStrengthTypeId = 1, compressiveStrengthTestTypeId = 1): CompStrRow {
    const row: CompStrRow = {
      compressiveStrengthTypeId,
      compressiveStrengthTestTypeId,
      crushTestTypeId: null,
      contamination: false,
      temperatureRampTime: null,
      temperature: this.testSettings.bhst,
      pressure: null,
      lengthOfTest: null,
      notes: '',
      isMuted: false,
      testInputMasterId: null,
      outputs: []
    };
    this.applyCompressiveStrengthTypeDefaults(row);
    return row;
  }

  private createDefaultFoamStability(foamedStabilityTypeId = 1): FoamStabilityRow {
    return {
      foamedStabilityTypeId,
      foamedDensity: this.testSettings.foamDensity,
      notes: '',
      isMuted: false,
      testInputMasterId: null,
      outputs: []
    };
  }

  private applyCompressiveStrengthTypeDefaults(row: CompStrRow): void {
    if (row.temperature == null) {
      row.temperature = this.testSettings.bhst;
    }

    if (this.isCompressiveStrengthUca(row)) {
      if (row.temperatureRampTime == null) {
        row.temperatureRampTime = this.compressiveStrengthDefaultRampTime;
      }
    } else {
      row.temperatureRampTime = null;
    }

    if (this.isCompressiveStrengthCrush(row)) {
      if (row.crushTestTypeId == null) {
        row.crushTestTypeId = 1;
      }
      if (row.lengthOfTest == null) {
        row.lengthOfTest = this.compressiveStrengthDefaultLengthOfTest;
      }
    } else {
      row.crushTestTypeId = null;
      row.lengthOfTest = null;
    }
  }

  private clearBlockedActionMessages(): void {
    this.blockedTestsActionMessage = '';
    this.blockedFoamSettingsActionMessage = '';
  }

  private clearSectionRows(kind: FoamTestSectionKind): void {
    const rows = kind === 'foamStability' ? this.foamStabilityTests : this.foamCompStrTests;
    rows.splice(0, rows.length);
  }

  private syncSectionSelectionFromRows(): void {
    this.sectionSelected['foamStability'] = this.foamStabilityTests.length > 0;
    this.sectionSelected['compressiveStrength'] = this.foamCompStrTests.length > 0;
  }
}
